import type { EnvelopeView } from '@argink/shared';
import type { HumanError } from './errors';

export type LoadPhase = 'loading' | 'loaded' | 'error';

export type SignPhase =
  | 'idle'
  | 'opening_session'
  | 'authorizing_agent'
  | 'signing_agent'
  | 'revision_stale'
  | 'completing'
  | 'success'
  | 'error';

export type CeremonyScreen =
  | { kind: 'loading' }
  | { kind: 'load_error' }
  | { kind: 'empty' }
  | { kind: 'signer_not_found' }
  | { kind: 'observer' }
  | { kind: 'not_started' }
  | { kind: 'envelope_expired' }
  | { kind: 'envelope_cancelled' }
  | { kind: 'envelope_declined' }
  | { kind: 'signer_declined' }
  | { kind: 'signer_expired' }
  | { kind: 'success' }
  | { kind: 'stage_blocked' }
  | { kind: 'terminal_error' }
  | { kind: 'simple' }
  | { kind: 'qualified' };

export interface ResolveCeremonyScreenInput {
  loadPhase: LoadPhase;
  envelope: EnvelopeView | null;
  signer: EnvelopeView['signers'][number] | null;
  signPhase: SignPhase;
  signError: HumanError | null;
  stageBlocked: boolean;
  isSimples: boolean;
}

/**
 * Decisão PURA de qual tela mostrar para o signatário — extraída da cadeia de
 * `if (...) return shell(...)` que existia inline em `SignPage.tsx`, para virar
 * testável sem DOM/rede/hooks (ver `ceremonyScreen.test.ts`). É a máquina de
 * estados da cerimônia (mandato §4/§12, IMVP-15/16/17): a ORDEM das condições
 * importa e é sensível a reordenação — não reordene sem atualizar os testes.
 *
 * Cobertura, na ordem exata:
 *  1. carregamento do envelope (loading/error/empty/signer ausente);
 *  2. observer nunca vê ação de assinar (barrado antes de qualquer outro estado
 *     do envelope, IMVP-16);
 *  3. estados terminais do envelope (não iniciado, expirado, cancelado, recusado);
 *  4. estados terminais do signatário (recusou, expirou);
 *  5. sucesso (assinado OU signPhase de sucesso) — tem prioridade sobre
 *     stage bloqueado e sobre erro não-terminal;
 *  6. stage bloqueado (fail-closed — sem tentativa automática, IMVP-17);
 *  7. erro terminal (não permite retry de certificado nem de assinatura);
 *  8. modalidade: Simples ou Qualificada — cada uma resolve o resto do fluxo
 *     internamente a partir do estado local do componente.
 */
export function resolveCeremonyScreen(input: ResolveCeremonyScreenInput): CeremonyScreen {
  const { loadPhase, envelope, signer, signPhase, signError, stageBlocked, isSimples } = input;

  if (loadPhase === 'loading') return { kind: 'loading' };
  if (loadPhase === 'error') return { kind: 'load_error' };
  if (!envelope) return { kind: 'empty' };
  if (!signer) return { kind: 'signer_not_found' };

  if (signer.role === 'observer') return { kind: 'observer' };
  if (envelope.status === 'draft' || envelope.status === 'ready') return { kind: 'not_started' };
  if (envelope.status === 'expired') return { kind: 'envelope_expired' };
  if (envelope.status === 'cancelled') return { kind: 'envelope_cancelled' };
  if (envelope.status === 'declined') return { kind: 'envelope_declined' };
  if (signer.status === 'declined') return { kind: 'signer_declined' };
  if (signer.status === 'expired') return { kind: 'signer_expired' };

  if (signer.status === 'signed' || signPhase === 'success') return { kind: 'success' };
  if (stageBlocked || signer.status === 'pending') return { kind: 'stage_blocked' };
  if (signPhase === 'error' && signError?.terminal) return { kind: 'terminal_error' };

  return isSimples ? { kind: 'simple' } : { kind: 'qualified' };
}
